import {
  type PropsWithChildren,
  type ReactNode,
  Suspense,
  lazy,
  useState,
} from 'react';

import { VisuallyHidden } from '@spotify-internal/encore-web';
import {
  splitComponent,
  useFeatureFlag,
} from '@spotify/confidence-flag-evaluation-react';

import { useIsWorkspaceAdmin } from '../../hooks/useIsWorkspaceAdmin';
import { useOnboardingSurvey } from '../../hooks/useOnboardingSurvey';
import { OnboardingProgress } from '../OnboardingProgress';
import {
  AgentWelcome,
  OnboardingSurvey,
  OnboardingSurveyStep,
} from '../OnboardingSurvey';

const ContextReview = lazy(() =>
  import('../ContextReview').then(module => ({
    default: module.ContextReview,
  })),
);

const ONBOARDING_STEPS = [
  { id: 'about', label: 'About you' },
  { id: 'company', label: 'Your company' },
] as const;

type OnboardingStep = (typeof ONBOARDING_STEPS)[number]['id'];

const AGENT_ONBOARDING_STEPS = ONBOARDING_STEPS.map(step => step.label);
const SURVEY_ONBOARDING_STEPS = AGENT_ONBOARDING_STEPS.slice(0, 1);

type OnboardingSurveyGateProps = PropsWithChildren<{ fallback?: ReactNode }>;

const SurveyGate = ({
  children,
  fallback = null,
}: OnboardingSurveyGateProps) => {
  const { status, workspaceName, isSaving, hasSaveError, saveAnswers } =
    useOnboardingSurvey();

  if (status === 'loading') {
    return <>{fallback}</>;
  }

  if (status === 'unanswered') {
    return (
      <OnboardingSurvey
        workspaceName={workspaceName}
        isSaving={isSaving}
        hasSaveError={hasSaveError}
        onSave={saveAnswers}
      />
    );
  }

  return <>{children}</>;
};

const GuidedOnboardingGate = ({
  children,
  fallback = null,
}: OnboardingSurveyGateProps) => {
  const { status, workspaceName, isSaving, hasSaveError, saveAnswers } =
    useOnboardingSurvey();
  const { enabled: chatsEnabled } = useFeatureFlag('chats', { enabled: false });
  const { enabled: projectsEnabled } = useFeatureFlag('projects', {
    enabled: false,
  });
  const { isWorkspaceAdmin, loading: isCheckingAdmin } = useIsWorkspaceAdmin();
  const companyStepEnabled =
    chatsEnabled && projectsEnabled && isWorkspaceAdmin;
  const [activeStep, setActiveStep] = useState<OnboardingStep | 'done' | null>(
    null,
  );

  if (
    activeStep === null &&
    (status === 'loading' ||
      (status === 'unanswered' && chatsEnabled && isCheckingAdmin))
  ) {
    return <>{fallback}</>;
  }

  const steps = companyStepEnabled
    ? AGENT_ONBOARDING_STEPS
    : SURVEY_ONBOARDING_STEPS;
  const currentStep =
    activeStep === 'done'
      ? undefined
      : (activeStep ?? (status === 'unanswered' ? 'about' : undefined));

  if (!currentStep) {
    return <>{children}</>;
  }

  const stepIndex = ONBOARDING_STEPS.findIndex(step => step.id === currentStep);
  const progress = <OnboardingProgress steps={steps} currentStep={stepIndex} />;

  const renderStep = (step: OnboardingStep): ReactNode => {
    if (step === 'company') {
      return (
        <Suspense fallback={fallback}>
          <ContextReview
            workspaceName={workspaceName}
            progress={progress}
            onBack={() => setActiveStep('about')}
            onContinue={() => setActiveStep('done')}
          />
        </Suspense>
      );
    }
    if (chatsEnabled) {
      return (
        <AgentWelcome
          orgName={workspaceName}
          renderProgress={questionProgress => (
            <OnboardingProgress
              steps={steps}
              currentStep={stepIndex}
              currentStepProgress={questionProgress}
            />
          )}
          isSaving={isSaving}
          hasSaveError={hasSaveError}
          onContinue={async ({ answers }) => {
            setActiveStep('about');
            if (await saveAnswers(answers)) {
              setActiveStep(companyStepEnabled ? 'company' : 'done');
            }
          }}
        />
      );
    }
    return (
      <OnboardingSurveyStep
        workspaceName={workspaceName}
        progress={progress}
        isSaving={isSaving}
        hasSaveError={hasSaveError}
        onSave={answers => {
          void saveAnswers(answers);
        }}
      />
    );
  };

  return (
    <>
      <VisuallyHidden aria-live="polite">
        {`Step ${stepIndex + 1} of ${steps.length}: ${steps[stepIndex]}`}
      </VisuallyHidden>
      {ONBOARDING_STEPS.slice(0, stepIndex + 1).map(({ id }) => (
        <div key={id} hidden={id !== currentStep}>
          {renderStep(id)}
        </div>
      ))}
    </>
  );
};

export const OnboardingSurveyGate = splitComponent<
  { enabled: boolean },
  OnboardingSurveyGateProps
>('guided-onboarding', { enabled: false }, ({ enabled }) =>
  enabled ? GuidedOnboardingGate : SurveyGate,
);
